import React, { useState, useEffect } from 'react';
import { auth, googleProvider } from '../firebaseConfig';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  sendEmailVerification, 
  onAuthStateChanged, 
  signOut
} from 'firebase/auth';
import { useNavigate, Link, useLocation } from 'react-router-dom'; 
import { getFirestore, doc, setDoc, getDoc } from 'firebase/firestore';
import Logo from '../assets/logo.png';
import GoogleLogo from '../assets/google-logo.svg';
import apple_logo from '../assets/apple_logo.png';
import google_play from "../assets/google_play.png";
import defaultUserImage from '../assets/default-user.png';  
import './Base.css';
import { useAuth } from '../authContext';


const Auth: React.FC= () => {
  const location = useLocation();
  const [isLogin, setIsLogin] = useState<boolean>(location.state?.isLogin ?? true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [showLogo, setShowLogo] = useState(false);
  const [showAppName, setShowAppName] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [showFront, setShowFront] = useState(true);
  const [showAuthForm, setShowAuthForm] = useState(false);
  const { isLoggedIn, setIsLoggedIn } = useAuth();
  const navigate = useNavigate();
  
  // Auto logout after 5 days
  const autoLogout = () => {
    const lastLogin = localStorage.getItem('lastLogin');
    const currentTime = new Date().getTime();
    
    if (lastLogin) {
      const timeDiff = currentTime - parseInt(lastLogin);
      const fiveDaysInMs = 5 * 24 * 60 * 60 * 1000; // 5 days in milliseconds
      if (timeDiff > fiveDaysInMs) {
        signOut(auth)
          .then(() => {
            localStorage.removeItem('lastLogin'); // Clear last login timestamp
            alert('You have been logged out due to inactivity.');
            navigate('/auth'); // Redirect to login page
          })
          .catch((error) => {
            console.error('Error during logout:', error);
          });
      }
    }
  };

  useEffect(() => {
    if (!showFront) {
      setShowAuthForm(true);
    }
    console.log('showAuth Form', showAuthForm);
  }, [showFront]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        autoLogout(); // Check if user should be logged out
        navigate('/');
      }
    });
  
    const logoTimer = window.setTimeout(() => setShowLogo(true), 500);
    const nameTimer = window.setTimeout(() => setShowAppName(true), 2000);

    return () => {
      window.clearTimeout(logoTimer);
      window.clearTimeout(nameTimer);
      unsubscribe();
    };
  }, [auth, navigate]);

  const handleEmailAuth = async () => {
    try {
      if (isLogin) {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        if (!user.emailVerified) {
          setEmailNotVerified(true);
          alert('Please verify your email before logging in.');
          return;
        }

        localStorage.setItem('lastLogin', new Date().getTime().toString()); // Save last login time
        setIsLoggedIn(true);
        navigate('/');
      } else {
        if (password !== confirmPassword) {
          alert('Passwords do not match');
          return;
        }

        // Create a new user with email and password
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Send email verification
        await sendEmailVerification(user);

        // Save user profile to Firestore
        const firestore = getFirestore();
        const userDocRef = doc(firestore, 'users', user.uid);
        await setDoc(userDocRef, {
          firstName: name,
          email: user.email,
          bio: '',
          imageUrl: defaultUserImage,
          subscriptionStatus: 'Starter',
        });

        alert('Account created successfully. Please check your email for verification.');
        navigate('/auth'); // Navigate to login page after signup
      }
    } catch (error: any) {
      console.error('Authentication error:', error);
      alert(`Please verify your email and password and try again.`);
    }
  };

  const resendVerificationEmail = async () => {
    try {
      const user = auth.currentUser;
      if (user && !user.emailVerified) {
        await sendEmailVerification(user);
        alert('Verification email resent. Please check your inbox.');
      } else {
        alert('Email is already verified or user is not logged in.');
      }
    } catch (error: any) {
      console.error('Error resending verification email:', error);
      alert(`Failed to resend verification email.`);
    }
  };

  const handleGoogleAuth = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Extract the user's information
      const displayName = user.displayName;
      const email = user.email;
      const photoURL = user.photoURL;

      // Initialize Firestore
      const firestore = getFirestore();
      const userDocRef = doc(firestore, 'users', user.uid);

      // Retrieve the current document for the user
      const userDoc = await getDoc(userDocRef);

      // Prepare update data
      const updateData: Record<string, any> = { email }; // Always update email

      // Conditionally update name and imageUrl only if they are not already set
      if (!userDoc.exists() || !userDoc.data()?.name) {
        updateData.name = displayName;
      }

      if (!userDoc.exists() || !userDoc.data()?.imageUrl) {
        updateData.imageUrl = photoURL;
      }

      // Save or update user info in Firestore
      await setDoc(userDocRef, updateData, { merge: true });

      // Save last login time
      localStorage.setItem('lastLogin', new Date().getTime().toString());
      setIsLoggedIn(true);
      // Navigate to home
      navigate('/');
    } catch (error: any) {
      console.error('Google authentication error:', error);
      alert(`Google authentication failed.`);
    }
  };

  const buttonStyle = {
    width: '250px',
    padding: '10px',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    marginBottom: '10px',
    marginLeft: '0px',
    fontWeight: 'bold',
    transition: 'background-color 0.3s ease',
  };

  const buttonHoverStyle = {
    backgroundColor: '#45a049',
  };

  const googleButtonStyle = {
    ...buttonStyle,
    width: '200px',
    backgroundColor: 'black',
    marginLeft: '0px',
    borderradius: '2px',
    border: '1px solid #ccc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const logoStyle: React.CSSProperties = {
    width: '60px',
    marginBottom: '10px',
    opacity: showLogo ? 1 : 0,
    transition: 'opacity 1s ease',
    visibility: showLogo ? 'visible' : 'hidden',
  };

  const appNameStyle: React.CSSProperties = {
    fontSize: '48px',
    marginBottom: '-30px',
    marginTop: '-10px',
    opacity: showAppName ? 1 : 0,
    transition: 'opacity 1s ease',
    visibility: showAppName ? 'visible' : 'hidden',
    animation: showAppName ? 'moveUp 1s forwards' : 'none',
  };

  const inputStyle: React.CSSProperties = {
    width: '180%',
    padding: '10px',
    marginBottom: '10px',
    marginLeft: '-55px',
    borderRadius: '10px', // Rounded corners for input boxes
    border: '1px solid #ccc', // Optional: Add a border
  };

  const dividerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    color: '#FFFFFF', // White color for the text and line
    margin: '20px 0',
    width: '10%',
  };

  const lineStyle: React.CSSProperties = {
    flex: 1,
    height: '1px',
    backgroundColor: '#FFFFFF', // White color for the line
  };

  const orTextStyle: React.CSSProperties = {
    margin: '0 10px',
    whiteSpace: 'nowrap',
  };

  return (
    <div style={{ 
      maxWidth: '100%', 
      margin: '0 auto', 
      padding: '20px', 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      height: '100vh', 
      textAlign: 'center',
      backgroundColor: '#000000', // Change background color to black
      backgroundImage: 'url(https://www.transparenttextures.com/patterns/stardust.png)',
      color: '#FFFFFF', // Change text color to white
    }}>
     <div style={{ marginBottom: '10px', position: 'relative', top: '-30px' }}>
       {/* Logo with fade-in effect */}
       <img
          src={Logo}
          alt="CompreQnA Logo"
          style={logoStyle}
        />
        {/* App name with delayed fade-in effect */}
        <h1 style={appNameStyle}>
          CompreQnA
        </h1>
      </div>

      {showAppName && (
        <>
          <button
            onClick={handleGoogleAuth}
            style={googleButtonStyle}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'black')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'black')}
          >
            <img src={GoogleLogo} alt="Google logo" style={{ width: '40px', marginRight: '10px' }} />
            Sign in with Google
          </button>

          {/* Divider with "OR" */}
          <div style={dividerStyle}>
            <div style={lineStyle}></div>
            <div style={orTextStyle}>OR</div>
            <div style={lineStyle}></div>
          </div>

          {!isLogin && (
            <div>
              <input
                type="text"
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={inputStyle}
              />
            </div>
          )}

          <div>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
            />
          </div>
          {!isLogin && (
            <div>
              <input
                type="password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={inputStyle}
              />
            </div>
          )}
          <button
            onClick={handleEmailAuth}
            style={buttonStyle}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = buttonHoverStyle.backgroundColor)}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#4CAF50')}
          >
            {isLogin ? 'Login' : 'Sign Up'}
          </button>

          {/* If the user's email is not verified, show this button */}
          {emailNotVerified && (
            <button
              onClick={resendVerificationEmail}
              style={{ ...buttonStyle, backgroundColor: '#FFA500' }}
            >
              Resend Verification Email
            </button>
          )}

          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <h2 style={{ marginBottom: '-50px', fontSize: '14px', color: '#FFFFFF' }}>Coming Soon!!!</h2> {/* Updated text color to white */}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '40px' }}>
            <img
              src={apple_logo}
              alt="Apple App Store logo"
              style={{ width: '150px', height: 'auto', marginLeft: "-150px" }}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '-45px' }}>
            <img
              src={google_play}
              alt="Google Play Store logo" 
              style={{ width: '150px', height: 'auto', marginRight: "-150px" }}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <Link to="/support" style={{ marginRight: '30px', color: '#FFFFFF', textDecoration: 'none',  marginTop: '20px' }}>Support</Link> {/* Updated link color */}
            <Link to="/privacy-policy" style={{ marginRight: '30px', color: '#FFFFFF', textDecoration: 'none',  marginTop: '20px' }}>Privacy Policy</Link> {/* Updated link color */}
            <Link to="/terms" style={{ color: '#FFFFFF', textDecoration: 'none',  marginTop: '20px' }}>Terms</Link> {/* Updated link color */}
          </div>
        </>
      )}
    </div>
  );
};

export default Auth;
